import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";
import { useEventTypesQuery } from "apis/eventType";
import { useContractorCompaniesQuery } from "apis/contractorCompany";

const formatSize = (size) => {
  if (!size) return "";
  return size
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
};

export default function CampaignEventsEdit({ initialData = {}, onSubmit }) {
  const { data: eventTypes, isLoading: isEventTypesLoading } =
    useEventTypesQuery();

  const { data: companies, isLoading: isCompaniesLoading } =
    useContractorCompaniesQuery("active");

  const {
    register,
    unregister,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      eventTypeId:
        initialData.eventTypeId?._id || initialData.eventTypeId || "",
      name: initialData.name || "",
      // Edited in minutes; stored on the backend in hours.
      durationMinutes: initialData.durationHours
        ? Math.round(initialData.durationHours * 60)
        : "",
      quantity: initialData.quantity || 1,
      size: initialData.size || "",
      contractorCompanyId:
        initialData.contractorCompanyId?._id ||
        initialData.contractorCompanyId ||
        "",
      isSystem: initialData.isSystem || false,
    },
  });

  const watchEventTypeId = watch("eventTypeId");
  const watchQuantity = watch("quantity");
  const watchSize = watch("size");

  const selectedEventType = eventTypes?.find(
    (et) => et._id === watchEventTypeId,
  );

  const sizeOptions = selectedEventType?.sizeOptions || [];

  // Read requiresDuration directly from the selected event type
  const needsDuration = selectedEventType?.requiresDuration ?? true;

  // Company select is only relevant when the event type requires a contractor
  const needsContractor = selectedEventType?.requiresContractor ?? false;

  // Options for the SearchSelect ({ label, value } shape)
  const companyOptions = (companies || []).map((c) => ({
    label: c.name,
    value: c._id,
  }));

  // Auto-generate name whenever EventType, quantity, or size changes
  useEffect(() => {
    if (!selectedEventType) return;
    const qty = parseInt(watchQuantity, 10) || 1;
    const parts = [selectedEventType.name];
    if (watchSize) parts.push(formatSize(watchSize));
    if (qty > 1) parts.push(`x${qty}`);
    setValue("name", parts.join(" "));
  }, [watchEventTypeId, watchQuantity, watchSize, selectedEventType, setValue]);

  // Reset size when event type changes and new type has different options
  useEffect(() => {
    if (selectedEventType) {
      const currentSize = watch("size");
      if (sizeOptions.length === 0) {
        setValue("size", "");
      } else if (currentSize && !sizeOptions.includes(currentSize)) {
        setValue("size", "");
      }
    }
  }, [watchEventTypeId, selectedEventType, sizeOptions, setValue, watch]);

  // Unregister duration field and clear its value/errors when not needed
  useEffect(() => {
    if (!needsDuration) {
      setValue("durationMinutes", "");
      clearErrors("durationMinutes");
      unregister("durationMinutes");
    }
  }, [needsDuration, setValue, clearErrors, unregister]);

  // Clear company selection when the event type doesn't require a contractor
  useEffect(() => {
    if (!needsContractor) {
      setValue("contractorCompanyId", "");
      clearErrors("contractorCompanyId");
    }
  }, [needsContractor, setValue, clearErrors]);

  const handleEventTypeChange = (e) => {
    const selectedId = e.target.value;
    setValue("eventTypeId", selectedId);

    const selectedET = eventTypes?.find((et) => et._id === selectedId);
    if (
      selectedET &&
      selectedET.requiresDuration &&
      selectedET.defaultDurationMinutes
    ) {
      setValue("durationMinutes", selectedET.defaultDurationMinutes);
    }
  };

  const submitForm = (data) => {
    const transformedData = {
      eventTypeId: data.eventTypeId,
      name: data.name,
      // Backend stores hours
      durationHours: data.durationMinutes
        ? parseFloat(data.durationMinutes) / 60
        : null,
      quantity: data.quantity ? parseInt(data.quantity, 10) : 1,
      size: data.size || null,
      contractorCompanyId: data.contractorCompanyId || null,
      isSystem: !!data.isSystem,
    };
    onSubmit(transformedData);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-6">
      {/* Event Type */}
      <div className="space-y-2">
        <label htmlFor="eventTypeId" className="form-label">
          Event Type *
        </label>
        <select
          id="eventTypeId"
          {...register("eventTypeId", {
            required: "Event type is required",
          })}
          onChange={handleEventTypeChange}
          className={`form-input border ${
            errors.eventTypeId ? "border-red-500" : "border-mediumgray"
          }`}
          disabled={isEventTypesLoading}
        >
          <option value="">
            {isEventTypesLoading ? "Loading..." : "Select an event type"}
          </option>
          {(eventTypes || []).map((et) => (
            <option key={et._id} value={et._id}>
              {et.name}
            </option>
          ))}
        </select>
        {errors.eventTypeId && (
          <p className="form-error-message">{errors.eventTypeId.message}</p>
        )}
        {selectedEventType && (
          <div className="text-sm text-darkgray space-y-1 mt-1">
            <p>
              Category:{" "}
              <span className="font-medium">{selectedEventType.category}</span>
              {selectedEventType.requiresContractor && (
                <span className="ml-2 text-orange-600">
                  • Requires contractor ({selectedEventType.contractorService})
                </span>
              )}
            </p>
            {selectedEventType.specificHours?.length > 0 && (
              <p>
                Restricted hours: {selectedEventType.specificHours.length} time
                slots
              </p>
            )}
          </div>
        )}
      </div>

      {/* Contractor Company — only when the event type requires a contractor */}
      {needsContractor && (
        <div className="space-y-2">
          <label className="form-label">Contractor Company *</label>
          <Controller
            name="contractorCompanyId"
            control={control}
            rules={{
              required: needsContractor ? "Company is required" : false,
            }}
            render={({ field }) => (
              <SearchSelect
                options={companyOptions}
                value={
                  companyOptions.find((o) => o.value === field.value) || null
                }
                onChange={(option) => field.onChange(option?.value || "")}
                placeholder={
                  isCompaniesLoading ? "Loading..." : "Select a company"
                }
                isClearable
                disabled={isCompaniesLoading}
                className={errors.contractorCompanyId ? "border-red-500" : ""}
              />
            )}
          />
          {errors.contractorCompanyId && (
            <p className="form-error-message">
              {errors.contractorCompanyId.message}
            </p>
          )}
        </div>
      )}

      {/* Quantity */}
      <div className="space-y-2">
        <label htmlFor="quantity" className="form-label">
          Quantity *
        </label>
        <input
          type="number"
          id="quantity"
          min="1"
          {...register("quantity", {
            required: "Quantity is required",
            min: { value: 1, message: "Minimum quantity is 1" },
          })}
          className={`form-input border ${
            errors.quantity ? "border-red-500" : "border-mediumgray"
          }`}
          placeholder="e.g., 1, 5, 10"
        />
        {errors.quantity && (
          <p className="form-error-message">{errors.quantity.message}</p>
        )}
      </div>

      {/* Size — only shown when the selected event type has sizeOptions */}
      {sizeOptions.length > 0 && (
        <div className="space-y-2">
          <label htmlFor="size" className="form-label">
            Size
          </label>
          <select
            id="size"
            {...register("size")}
            className="form-input border border-mediumgray"
          >
            <option value="">Select a size</option>
            {sizeOptions.map((size) => (
              <option key={size} value={size}>
                {formatSize(size)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Event Name (auto-generated, disabled) */}
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Event Name
        </label>
        <input
          type="text"
          id="name"
          {...register("name", { required: "Event name is required" })}
          className="form-input border border-mediumgray bg-lightgray"
          disabled
        />
        <p className="text-sm text-darkgray">
          Auto-generated from event type, size, and quantity.
        </p>
      </div>

      {/* Duration — only shown when the event type requires it */}
      {needsDuration && (
        <div className="space-y-2">
          <label htmlFor="durationMinutes" className="form-label">
            Duration (minutes) *
          </label>
          <input
            type="number"
            id="durationMinutes"
            step="5"
            min="5"
            max="720"
            {...register("durationMinutes", {
              required: "Duration is required",
              min: {
                value: 5,
                message: "Duration must be at least 5 minutes",
              },
              max: {
                value: 720,
                message: "Duration cannot exceed 720 minutes (12 hours)",
              },
            })}
            className={`form-input border ${
              errors.durationMinutes ? "border-red-500" : "border-mediumgray"
            }`}
            placeholder="e.g., 30, 60, 90"
          />
          {errors.durationMinutes && (
            <p className="form-error-message">
              {errors.durationMinutes.message}
            </p>
          )}
          <p className="text-sm text-darkgray">
            Enter duration in minutes (e.g., 30 for half an hour, 90 for 1.5
            hours)
          </p>
        </div>
      )}

      {/* isSystem flag */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...register("isSystem")}
            className="h-4 w-4"
          />
          <span className="form-label mb-0">System event</span>
        </label>
        <p className="text-sm text-darkgray">
          System events are defined by Beleef.
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end mt-6 pt-4 border-t border-mediumgray">
        <Button type="submit" className="black-button">
          {initialData._id ? "Update Campaign Event" : "Create Campaign Event"}
        </Button>
      </div>
    </form>
  );
}
